import { useId, type TextareaHTMLAttributes } from 'react';

import { cx } from '../../lib/cx';

import styles from './TextArea.module.css';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Shown above the field; it is the field's name. */
  label: string;
  /** A line under the field: what to write, or what is wrong with it. */
  hint?: string;
  /** What is written is wrong: a red border, and `aria-invalid` for assistive technology. */
  invalid?: boolean;
  /** The label is read by assistive technology but not shown: where the layout names the field. */
  hideLabel?: boolean;
  /** Text in the monospaced face: code, data. */
  monospace?: boolean;
}

/** Several lines of text with their label: a reason, a note, a comment. */
export function TextArea({
  label,
  hint,
  className,
  id: given,
  rows = 3,
  invalid = false,
  hideLabel = false,
  monospace = false,
  ...rest
}: TextAreaProps) {
  const generated = useId();
  const id = given ?? generated;
  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={id} className={cx(styles.label, hideLabel && styles.hiddenLabel)}>
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        aria-describedby={hint ? `${id}-hint` : undefined}
        aria-invalid={invalid || undefined}
        className={cx(styles.input, invalid && styles.invalid, monospace && styles.monospace)}
        {...rest}
      />
      {hint ? (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
